import {
    AUDIO_JITTER_THRESHOLDS,
    AUDIO_PACKETS_CONCEALED_THRESHOLDS,
    AUDIO_PACKETS_LOST_THRESHOLDS,
    BANDWIDTH_THRESHOLDS,
    BW_SENTINEL_CAP_KBPS,
    ENCODE_BW_PERCENT_THRESHOLDS,
    RTT_THRESHOLDS,
    VIDEO_DROP_THRESHOLDS,
    VIDEO_PACKETS_CONCEALED_THRESHOLDS,
    VIDEO_PACKETS_LOST_THRESHOLDS,
    VIDEO_PACKETS_REORDERED_THRESHOLDS,
} from './constants';
import type {NetworkStats, ParticipantQuality, QualityLevel} from './types';

// ─── Low-level helpers ────────────────────────────────────────────────────────

/** Returns delta only if non-negative; negative means counter reset — skip that sample. */
const safeDelta = (current: number | undefined, prev: number | undefined): number | undefined => {
    if (current == null || prev == null) return undefined;
    const d = current - prev;
    return d >= 0 ? d : undefined;
};

const bpsToKbps = (bps: number | undefined): number | undefined => (bps !== undefined ? bps / 1000 : undefined);

/** kbps → human-readable string */
const formatBitrate = (kbps?: number): string => {
    if (kbps === undefined) return '—';
    if (kbps === 0) return '0 kbps';
    if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`;
    return `${Math.round(kbps)} kbps`;
};

// ─── Quality scoring ──────────────────────────────────────────────────────────

/** Returns the worse of two quality levels (worst-wins rule). */
const worse = (a: QualityLevel, b: QualityLevel): QualityLevel => {
    const rank: Record<QualityLevel, number> = {poor: 0, fair: 1, good: 2, unknown: 3};
    return rank[a] <= rank[b] ? a : b;
};

/**
 * Scores a single participant stream.
 * Packet counts must be DELTA values; micJitter is real-time (seconds).
 */
const scoreParticipant = (p: Omit<ParticipantQuality, 'quality'>): QualityLevel => {
    let quality: QualityLevel = 'good';
    const w = (level: QualityLevel) => {
        quality = worse(quality, level);
    };

    // Video – packet loss delta/2s
    const vLost = p.videoPacketsLost ?? 0;
    if (vLost > VIDEO_PACKETS_LOST_THRESHOLDS.poorAbove) w('poor');
    else if (vLost > VIDEO_PACKETS_LOST_THRESHOLDS.fairAbove) w('fair');

    // Video – concealed delta/2s (raw cumulative is always large; only delta matters)
    const vConcealed = p.videoPacketsConcealed ?? 0;
    if (vConcealed > VIDEO_PACKETS_CONCEALED_THRESHOLDS.poorAbove) w('poor');
    else if (vConcealed > VIDEO_PACKETS_CONCEALED_THRESHOLDS.fairAbove) w('fair');

    // Video – reordered delta/2s
    const vReordered = p.videoPacketsReordered ?? 0;
    if (vReordered > VIDEO_PACKETS_REORDERED_THRESHOLDS.poorAbove) w('poor');
    else if (vReordered > VIDEO_PACKETS_REORDERED_THRESHOLDS.fairAbove) w('fair');

    // Audio – jitter (real-time seconds → ms)
    const jitterMs = (p.micJitter ?? 0) * 1000;
    if (jitterMs > AUDIO_JITTER_THRESHOLDS.poorAbove) w('poor');
    else if (jitterMs > AUDIO_JITTER_THRESHOLDS.fairAbove) w('fair');

    // Audio – packet loss delta/2s
    const mLost = p.micPacketsLost ?? 0;
    if (mLost > AUDIO_PACKETS_LOST_THRESHOLDS.poorAbove) w('poor');
    else if (mLost > AUDIO_PACKETS_LOST_THRESHOLDS.fairAbove) w('fair');

    // Audio – concealed delta/2s
    const mConcealed = p.micPacketsConcealed ?? 0;
    if (mConcealed > AUDIO_PACKETS_CONCEALED_THRESHOLDS.poorAbove) w('poor');
    else if (mConcealed > AUDIO_PACKETS_CONCEALED_THRESHOLDS.fairAbove) w('fair');

    return quality;
};

type Scorer = (level: QualityLevel, reason: string) => void;

const scoreRtt = (rtt: number | undefined, w: Scorer): void => {
    if (rtt === undefined) return;
    if (rtt > RTT_THRESHOLDS.poorAbove) w('poor', `RTT ${rtt}ms > ${RTT_THRESHOLDS.poorAbove}ms`);
    else if (rtt > RTT_THRESHOLDS.fairAbove) w('fair', `RTT ${rtt}ms > ${RTT_THRESHOLDS.fairAbove}ms`);
};

const scoreBandwidth = (stats: NetworkStats, w: Scorer): void => {
    const sendKbps = Math.min(stats.sendBitRateAvailable, BW_SENTINEL_CAP_KBPS);
    if (sendKbps < BANDWIDTH_THRESHOLDS.poorBelow)
        w('poor', `send ${Math.round(sendKbps)}kbps < ${BANDWIDTH_THRESHOLDS.poorBelow}kbps`);
    else if (sendKbps < BANDWIDTH_THRESHOLDS.fairBelow)
        w('fair', `send ${Math.round(sendKbps)}kbps < ${BANDWIDTH_THRESHOLDS.fairBelow}kbps`);

    if (stats.availableEncodeBwPercent !== undefined) {
        if (stats.availableEncodeBwPercent < ENCODE_BW_PERCENT_THRESHOLDS.poorBelow)
            w('poor', `encodeBw ${stats.availableEncodeBwPercent}% < ${ENCODE_BW_PERCENT_THRESHOLDS.poorBelow}%`);
        else if (stats.availableEncodeBwPercent < ENCODE_BW_PERCENT_THRESHOLDS.fairBelow)
            w('fair', `encodeBw ${stats.availableEncodeBwPercent}% < ${ENCODE_BW_PERCENT_THRESHOLDS.fairBelow}%`);
    }
};

/**
 * Scores all available signals and returns the worst quality level.
 * All bitrates must already be in kbps. Packet counts must be deltas.
 * Optionally collects human-readable reasons for any degradation into `reasons`.
 */
const getQualityLevel = (stats: NetworkStats, reasons?: string[]): QualityLevel => {
    let quality: QualityLevel = 'good';
    const w = (level: QualityLevel, reason: string) => {
        quality = worse(quality, level);
        reasons?.push(`[${level}] ${reason}`);
    };

    const hasParticipants = (stats.participants?.length ?? 0) > 0;

    // Without participants the channel is idle — bandwidth and encode% read 0 or defaults
    // and would falsely degrade the indicator, so skip them until the call is active.
    if (!hasParticipants) {
        if (stats.rtt === undefined) return 'unknown';
        scoreRtt(stats.rtt, w);
        return quality;
    }

    if (stats.sendBitRateAvailable === undefined) return 'unknown';

    scoreBandwidth(stats, w);
    scoreRtt(stats.rtt, w);

    // Rate-shaper packet drop
    if (stats.videoDropPct !== undefined) {
        if (stats.videoDropPct > VIDEO_DROP_THRESHOLDS.poorAbove)
            w('poor', `pktDrop ${stats.videoDropPct.toFixed(1)}% > ${VIDEO_DROP_THRESHOLDS.poorAbove}%`);
        else if (stats.videoDropPct > VIDEO_DROP_THRESHOLDS.fairAbove)
            w('fair', `pktDrop ${stats.videoDropPct.toFixed(1)}% > ${VIDEO_DROP_THRESHOLDS.fairAbove}%`);
    }

    // Per-participant streams
    for (const p of stats.participants ?? []) {
        if (p.quality !== 'unknown') w(p.quality, `participant "${p.name}" is ${p.quality}`);
    }

    return quality;
};

export {bpsToKbps, formatBitrate, getQualityLevel, safeDelta, scoreParticipant};
